import { useEffect, useRef, useState, type ChangeEvent, type ClipboardEvent, type FormEvent } from "react";
import { BookOpen, Bot, BrainCircuit, ChevronDown, Gauge, HelpCircle, ImagePlus, LifeBuoy, Map, RotateCcw, ScanSearch, Send, Sparkles, Wand2, X } from "lucide-react";
import type { AssistantChatResponse, AssistantContextResponse, AssistantNextAction, PlatformAction, SessionView } from "@control-plane/contracts";
import { api, csrfHeader } from "../lib/api";
import { businessLabel } from "../lib/displayNames";
import { appNavigate } from "../lib/navigate";

type ChatImage = { name: string; mimeType: string; dataUrl: string };
type AssistantProvenance = Pick<AssistantChatResponse, "readOnly" | "sources" | "speculation" | "nextActions">;
type ChatMessage = { role: "user" | "assistant"; content: string; images?: ChatImage[]; provenance?: AssistantProvenance };
type CapabilityTone = NonNullable<AssistantContextResponse["capabilityCards"]>[number]["tone"];

const assistantAskEvent = "control-plane:assistant-ask";

export function askAssistant(prompt: string) {
  window.dispatchEvent(new CustomEvent(assistantAskEvent, { detail: { prompt } }));
}

const beginnerTasks = [
  { icon: Map, title: "第一次用，先做什么", prompt: "我是第一次使用总管后台，请用小白能懂的话告诉我先看哪里、先做哪 3 件事、每一步怎么确认做对了。" },
  { icon: HelpCircle, title: "这个页面怎么用", prompt: "请按小白步骤解释当前页面：它管什么、什么时候用、先看哪里、能点哪些按钮、填错会怎样、做完怎么看成功。" },
  { icon: BrainCircuit, title: "帮我做决策", prompt: "请按当前页面帮我做一次辅助决策：现在最值得处理什么、哪些操作可以做、哪些操作建议暂缓、风险等级是什么、下一步应该点哪里。你只给判断和建议，不替我执行。" },
  { icon: LifeBuoy, title: "我遇到问题了", prompt: "我看到页面报错或不知道下一步，请先教我怎么描述问题，再列出常见原因、去哪里检查、哪些操作不要乱点。" },
];

const quickPrompts = [
  "新建租户时每个字段怎么填？",
  "怎么判断一个租户可以激活？",
  "怎么给 AI 助手换模型？",
  "看到密钥、凭据、服务账号这些词我该怎么理解？",
  "我点保存失败了应该先检查什么？",
  "哪些操作比较危险，点之前要注意什么？",
];

const welcome = "你好，我是平台总管后台的 AI 大脑。你不用懂技术名词也可以问我：这个页面是干什么的、字段怎么填、下一步点哪里、报错怎么处理、哪些操作有风险。我只做解释和引导，不会替你创建、修改或删除数据。";
// AI 服务商只显示中文业务名；未知服务商与模型编号不向普通操作者回显原始代码。
const assistantProviderLabels: Record<string, string> = { qwen: "通义千问", deepseek: "DeepSeek", kimi: "Kimi", zhipu: "智谱 GLM", mimo: "MiMo", custom_openai_compatible: "本地视觉模型", openai: "OpenAI" };
function assistantProviderName(provider: string): string { return businessLabel(assistantProviderLabels, provider, "AI 服务商待确认"); }
const capabilityIcons: Record<CapabilityTone, typeof Map> = { guide: BrainCircuit, check: ScanSearch, risk: LifeBuoy, cost: Gauge, image: ImagePlus, nav: Map, reason: Sparkles, dict: HelpCircle, score: Gauge, ops: BrainCircuit, engineer: ScanSearch, incident: LifeBuoy, tenant: Map, commander: BrainCircuit, draft: Sparkles, launch: Gauge, brief: BookOpen, decision: BrainCircuit, evidence: ScanSearch, escalate: LifeBuoy, duty: BookOpen, prevent: RotateCcw, system: BrainCircuit };

export function AssistantPanel({ session, currentPath, radar }: { session: SessionView; currentPath: string; radar?: AssistantContextResponse | null }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [images, setImages] = useState<ChatImage[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [meta, setMeta] = useState<{ provider: string; model: string } | null>(null);
  const [brain, setBrain] = useState<AssistantContextResponse | null>(null);
  const [brainError, setBrainError] = useState("");
  const [expandedGuide, setExpandedGuide] = useState<"fields" | "trouble" | "terms" | "guards" | "errors" | "launch" | "more" | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, busy, open]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBrainError("");
    api<AssistantContextResponse>(`/api/assistant/context?pagePath=${encodeURIComponent(currentPath)}`)
      .then((context) => { if (!cancelled) setBrain(context); })
      .catch((failure) => { if (!cancelled) setBrainError(failure instanceof Error ? failure.message : "AI 大脑上下文加载失败。"); });
    return () => { cancelled = true; };
  }, [currentPath, open]);
  useEffect(() => {
    const listener = (event: Event) => {
      const prompt = (event as CustomEvent<{ prompt?: string }>).detail?.prompt?.trim();
      if (!prompt) return;
      setOpen(true);
      window.setTimeout(() => void send(prompt), 0);
    };
    window.addEventListener(assistantAskEvent, listener);
    return () => window.removeEventListener(assistantAskEvent, listener);
  });

  if (!session.actions.includes("assistant.chat" as PlatformAction)) return null;

  async function send(text: string) {
    const content = text.trim() || (images.length ? "请看这张图片，告诉我这里是什么意思、有没有问题、下一步应该怎么处理。" : "");
    if (!content || busy) return;
    const userMessage: ChatMessage = images.length ? { role: "user", content, images } : { role: "user", content };
    const next: ChatMessage[] = [...messages, userMessage];
    setMessages(next);
    setInput("");
    setImages([]);
    setBusy(true);
    setError("");
    try {
      const streamed = await streamAssistantReply(next, currentPath, (delta) => {
        setMessages((current) => appendAssistantDelta(current, delta));
      }, (provider, model) => setMeta({ provider, model }));
      setMessages((current) => attachAssistantProvenance(current, streamed));
    } catch (failure) {
      try {
        const response = await api<AssistantChatResponse>("/api/assistant/chat", { method: "POST", body: JSON.stringify({ messages: next.slice(-6), pagePath: currentPath }) });
        await revealAssistantReply(next, response.reply, (partial) => setMessages([...next, { role: "assistant", content: partial }]));
        setMeta({ provider: response.provider, model: response.model });
        setMessages((current) => attachAssistantProvenance(current, response));
      } catch (fallbackFailure) {
        setError(fallbackFailure instanceof Error ? fallbackFailure.message : failure instanceof Error ? failure.message : "AI 助手暂时不可用，请稍后重试。");
      }
    } finally {
      setBusy(false);
    }
  }

  function handleAssistantAction(action: AssistantNextAction) {
    if (action.kind === "navigate" && action.pagePath) {
      appNavigate(action.pagePath);
      setOpen(false);
      return;
    }
    if ((action.kind === "ask" || action.kind === "open_manual") && action.prompt) {
      void send(action.prompt);
      return;
    }
    if (action.kind === "retry") {
      const lastUserQuestion = [...messages].reverse().find((message) => message.role === "user")?.content;
      if (lastUserQuestion) void send(lastUserQuestion);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    void send(input);
  }

  async function addImages(event: ChangeEvent<HTMLInputElement>) {
    await appendImageFiles(Array.from(event.target.files ?? []));
    event.target.value = "";
  }

  async function pasteImages(event: ClipboardEvent<HTMLInputElement>) {
    const files = Array.from(event.clipboardData.files).filter((file) => file.type.startsWith("image/"));
    if (!files.length) return;
    event.preventDefault();
    await appendImageFiles(files);
  }

  async function appendImageFiles(files: File[]) {
    const remaining = Math.max(0, 3 - images.length);
    const loaded = await Promise.all(files.slice(0, remaining).filter((file) => file.type.startsWith("image/") && file.size <= 2_000_000).map(readImageFile));
    setImages((current) => [...current, ...loaded].slice(0, 3));
  }

  return <>
    {open && <div className="assistant-panel" role="dialog" aria-label="AI 平台助手">
      <header>
        <div className="assistant-title"><Sparkles size={17} /><div><strong>AI 平台助手</strong><small>{meta ? `由 ${assistantProviderName(meta.provider)} 提供 · 问答与操作引导` : "问答与操作引导 · 不执行写操作"}</small></div></div>
        <div className="assistant-header-actions">
          {messages.length > 0 && <button className="icon-button" onClick={() => { setMessages([]); setError(""); setMeta(null); }} aria-label="清空对话" title="清空对话"><RotateCcw size={15} /></button>}
          <button className="icon-button" onClick={() => setOpen(false)} aria-label="关闭助手"><X size={16} /></button>
        </div>
      </header>
      <div className="assistant-messages" ref={listRef}>
        <div className="assistant-bubble assistant">{welcome}</div>
        {messages.length === 0 && <div className="assistant-start">
          {brain?.currentPage && <div className="assistant-page-card">
            <small>你现在在</small>
            <strong>{brain.currentPage.title}</strong>
            <span>{beginnerPageSummary(brain.currentPage.summary)}</span>
            <div>{brain.currentPage.firstLook.slice(0, 3).map((item) => <small key={item}>{item}</small>)}</div>
          </div>}
          {brain?.riskDigest?.some((risk) => risk.level !== "low") ? <div className="assistant-risk-list">
            {brain.riskDigest.filter((risk) => risk.level !== "low").slice(0, 2).map((risk) => <button key={`${risk.title}-${risk.pagePath}`} type="button" onClick={() => void send(`请解释这个平台风险，并告诉我去哪里处理：${risk.title}，${risk.detail}`)}>
              <span className={`assistant-risk-dot ${risk.level}`} /> <strong>{risk.title}</strong><small>{risk.detail}</small>
            </button>)}
          </div> : null}
          {brain?.diagnosticReport ? <div className="assistant-diagnostic">
            <button type="button" onClick={() => void send("请根据一键诊断报告，用小白能懂的话告诉我现在能不能上线、先处理哪几项、每项去哪个页面处理。")}>
              <Gauge size={16} />
              <span><strong>{brain.diagnosticReport.score} 分</strong><small>{brain.diagnosticReport.verdict}</small></span>
            </button>
            <div>{brain.diagnosticReport.items.slice(0, 3).map((item) => <button key={item.title} type="button" onClick={() => void send(`请解释诊断项「${item.title}」：${item.detail}。下一步应该怎么处理？`)}>
              <span className={`assistant-status-dot ${item.status}`} /><strong>{item.title}</strong><small>{item.action}</small>
            </button>)}</div>
          </div> : null}
          {brain?.commandCenter ? <div className="assistant-command">
            <button type="button" onClick={() => void send("请进入总管指挥官模式，按现在必须处理、今天处理、稍后优化告诉我当前后台优先级。")}>
              <BrainCircuit size={16} />
              <span><strong>总管指挥中心</strong><small>{brain.commandCenter.headline}</small></span>
            </button>
            <div>{brain.commandCenter.priorities.slice(0, 3).map((item) => <button key={`${item.level}-${item.title}`} type="button" onClick={() => void send(`请解释指挥中心任务「${item.title}」：${item.reason}。我应该怎么处理？`)}>
              <span className={`assistant-priority ${item.level}`}>{priorityLabel(item.level)}</span><strong>{item.title}</strong><small>{item.reason}</small>
            </button>)}</div>
          </div> : null}
          {brain?.operatorBriefing ? <div className="assistant-briefing">
            <button type="button" onClick={() => void send("请进入总管早会简报模式，把当前平台状态整理成我可以汇报给老板或客户的话，并告诉我今天先做什么。")}>
              <BookOpen size={16} />
              <span><strong>{brain.operatorBriefing.title}</strong><small>{brain.operatorBriefing.summary}</small></span>
              <em>{briefingLabel(brain.operatorBriefing.stance)}</em>
            </button>
            <p>{brain.operatorBriefing.report}</p>
          </div> : null}
          {brain?.dailyDigest ? <div className="assistant-daily-digest">
            <button type="button" onClick={() => void send(brain.dailyDigest.prompt)}>
              <ScanSearch size={16} />
              <span><strong>{brain.dailyDigest.title}</strong><small>{brain.dailyDigest.summary}</small></span>
              <em>{digestLabel(brain.dailyDigest.level)}</em>
            </button>
            <p>下次复查：{brain.dailyDigest.nextCheckAt}</p>
          </div> : null}
          {brain?.platformWideBrain ? <div className="assistant-wide-brain">
            <button type="button" onClick={() => void send("请进入全域大脑模式，按总管后台、租户端/ERP、运行时/API、部署/安全、代管协作给我做一次全链路判断。")}>
              <BrainCircuit size={16} />
              <span><strong>{brain.platformWideBrain.title}</strong><small>{brain.platformWideBrain.summary}</small></span>
            </button>
            <div>{brain.platformWideBrain.domains.slice(0, 5).map((item) => <button key={`${item.scope}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span>{scopeLabel(item.scope)}</span>
              <span><strong>{item.title}</strong><small>{item.firstChecks[0]}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.taskCenter ? <div className="assistant-task-center">
            <button type="button" onClick={() => void send("请进入 AI 任务中心模式，只告诉我今天最该先做的 3 件事，并按必须做、建议做、本周做、观察中分组。")}>
              <BrainCircuit size={16} />
              <span><strong>{brain.taskCenter.title}</strong><small>{brain.taskCenter.summary}</small></span>
            </button>
            <div>{brain.taskCenter.groups.filter((group) => group.items.length).slice(0, 4).map((group) => <button key={group.lane} type="button" onClick={() => void send(`请按「${group.title}」带我处理这些任务：${group.items.map((item) => item.title).join("、")}`)}>
              <span className={`assistant-lane ${group.lane}`}>{taskLaneLabel(group.lane)}</span>
              <span><strong>{group.title}</strong><small>{group.items.slice(0, 2).map((item) => item.title).join("、")}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.monitorAlerts?.length ? <div className="assistant-monitor-alerts">
            <strong>异常预警</strong>
            <div>{brain.monitorAlerts.slice(0, 4).map((item) => <button key={`${item.level}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span className={`assistant-alert ${item.level}`}>{alertLabel(item.level)}</span>
              <span><strong>{item.title}</strong><small>{item.firstAction}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.incidentWarRoom ? <div className={`assistant-war-room ${brain.incidentWarRoom.active ? "active" : ""}`}>
            <button type="button" onClick={() => void send(brain.incidentWarRoom.prompt)}>
              <LifeBuoy size={16} />
              <span><strong>{brain.incidentWarRoom.title}</strong><small>{brain.incidentWarRoom.summary}</small></span>
              <em>{warRoomLabel(brain.incidentWarRoom.severity)}</em>
            </button>
            <div>{brain.incidentWarRoom.stopLoss.slice(0, 3).map((item) => <small key={item}>{item}</small>)}</div>
          </div> : null}
          {brain?.copilotDrafts?.length ? <div className="assistant-copilot-drafts">
            <strong>待确认操作单</strong>
            <div>{brain.copilotDrafts.slice(0, 4).map((item) => <button key={`${item.action}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span className={`assistant-severity ${item.risk}`}>{item.risk === "high" ? "高" : "中"}</span>
              <span><strong>{item.title}</strong><small>{item.action} · 只生成，不执行</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.memoryCenter ? <div className="assistant-memory-center">
            <button type="button" onClick={() => void send("请进入平台记忆中枢模式，基于当前状态总结重复风险、经验教训、流程改进建议和下次可复用处理手册。")}>
              <BrainCircuit size={16} />
              <span><strong>{brain.memoryCenter.title}</strong><small>{brain.memoryCenter.summary}</small></span>
            </button>
            <div>{brain.memoryCenter.patterns.slice(0, 3).map((item) => <button key={item.title} type="button" onClick={() => void send(item.prompt)}>
              <span>经验</span><span><strong>{item.title}</strong><small>{item.nextTime}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.uxImprovementBoard?.length ? <div className="assistant-ux-board">
            <strong>页面改进建议</strong>
            <div>{brain.uxImprovementBoard.slice(0, 4).map((item) => <button key={`${item.priority}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span className={`assistant-priority ${item.priority}`}>{item.priority.toUpperCase()}</span>
              <span><strong>{item.title}</strong><small>{item.acceptance}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.actionQueue?.length ? <div className="assistant-action-queue">
            <strong>主动处理队列</strong>
            <div>{brain.actionQueue.slice(0, 4).map((item) => <button key={`${item.urgency}-${item.title}`} type="button" onClick={() => void send(`请按处理队列带我处理「${item.title}」：${item.why}。验收标准是：${item.acceptance}`)}>
              <span className={`assistant-priority ${item.urgency === "week" || item.urgency === "watch" ? "later" : item.urgency}`}>{queueLabel(item.urgency)}</span>
              <span><strong>{item.title}</strong><small>{item.acceptance}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.decisionPackets?.length ? <div className="assistant-decision-packets">
            <strong>动作决策包</strong>
            <div>{brain.decisionPackets.slice(0, 4).map((item) => <button key={`${item.decision}-${item.title}`} type="button" onClick={() => void send(item.askPrompt)}>
              <span className={`assistant-decision ${item.decision}`}>{decisionLabel(item.decision)}</span>
              <span><strong>{item.title}</strong><small>{item.reason}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.evidenceGaps?.length ? <div className="assistant-evidence-gaps">
            <strong>证据缺口</strong>
            <div>{brain.evidenceGaps.slice(0, 4).map((item) => <button key={`${item.severity}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span className={`assistant-severity ${item.severity}`}>{severityLabel(item.severity)}</span>
              <span><strong>{item.title}</strong><small>{item.missing}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.escalationPlans?.length ? <div className="assistant-escalations">
            <strong>升级沟通</strong>
            <div>{brain.escalationPlans.slice(0, 4).map((item) => <button key={`${item.owner}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span>{item.owner}</span>
              <span><strong>{item.title}</strong><small>{item.trigger}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.dutyChecklist?.length ? <div className="assistant-duty-list">
            <strong>值班手册</strong>
            <div>{brain.dutyChecklist.slice(0, 4).map((item) => <button key={`${item.cadence}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span>{cadenceLabel(item.cadence)}</span>
              <span><strong>{item.title}</strong><small>{item.passSignal}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.preventionPlans?.length ? <div className="assistant-prevention-plans">
            <strong>预防复盘</strong>
            <div>{brain.preventionPlans.slice(0, 4).map((item) => <button key={`${item.owner}-${item.title}`} type="button" onClick={() => void send(item.prompt)}>
              <span>{item.owner}</span>
              <span><strong>{item.title}</strong><small>{item.prevention}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.deepInsights?.length ? <div className="assistant-deep-insights">
            <strong>真实列表深读</strong>
            <div>{brain.deepInsights.slice(0, 4).map((item) => <button key={item.title} type="button" onClick={() => void send(item.prompt)}>
              <span className={`assistant-status-dot ${item.status}`} /><span><strong>{item.title}</strong><small>{item.detail}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.tenantBattleMap?.length ? <div className="assistant-war-map">
            <strong>租户作战地图</strong>
            <div>{brain.tenantBattleMap.map((item) => <button key={item.title} type="button" onClick={() => void send(`请解释租户作战地图里的「${item.title}」：${item.count} 个。含义是什么，下一步怎么处理？`)}>
              <span className={`assistant-status-dot ${item.status}`} /><span><strong>{item.count}</strong><small>{item.title}</small></span>
            </button>)}</div>
          </div> : null}
          {brainError && <div className="assistant-mini-error">{brainError}</div>}
          <div className="assistant-task-grid">
            {beginnerTasks.map(({ icon: Icon, title, prompt }) => <button key={title} type="button" onClick={() => void send(prompt)}>
              <Icon size={15} /><span>{title}</span>
            </button>)}
          </div>
          {brain?.guidedPlaybooks?.length ? <div className="assistant-playbooks">
            <strong>操作陪跑</strong>
            <div>{brain.guidedPlaybooks.slice(0, 4).map((playbook) => <button key={playbook.title} type="button" onClick={() => void send(playbook.prompt)} title={playbook.steps.join(" / ")}>
              <Sparkles size={14} /><span><strong>{playbook.title}</strong><small>{playbook.goal}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.draftTemplates?.length ? <div className="assistant-drafts">
            <strong>草稿中心</strong>
            <div>{brain.draftTemplates.slice(0, 6).map((draft) => <button key={draft.title} type="button" onClick={() => void send(draft.prompt)} title={draft.scenario}>
              <Sparkles size={13} /><span><strong>{draft.title}</strong><small>{draft.outputHint}</small></span>
            </button>)}</div>
          </div> : null}
          {brain?.capabilityCards?.length ? <div className="assistant-capabilities">
            <div><Wand2 size={14} /><strong>AI 能力</strong><small>点一下就让助手按这个方向处理</small></div>
            <div>{brain.capabilityCards.map((capability) => {
              const Icon = capabilityIcons[capability.tone] ?? Sparkles;
              return <button key={capability.title} type="button" onClick={() => void send(capability.prompt)} title={capability.description}>
                <Icon size={14} /><span><strong>{capability.title}</strong><small>{capability.description}</small></span>
              </button>;
            })}</div>
          </div> : null}
          <div className="assistant-guide-switches">
            {brain?.currentPage?.fields.length ? <button type="button" onClick={() => setExpandedGuide(expandedGuide === "fields" ? null : "fields")}>本页字段<ChevronDown size={13} /></button> : null}
            {brain?.troubleshooting?.length ? <button type="button" onClick={() => setExpandedGuide(expandedGuide === "trouble" ? null : "trouble")}>常见问题<ChevronDown size={13} /></button> : null}
            {brain?.termDictionary?.length ? <button type="button" onClick={() => setExpandedGuide(expandedGuide === "terms" ? null : "terms")}>小白词典<ChevronDown size={13} /></button> : null}
            {brain?.guardrails?.length ? <button type="button" onClick={() => setExpandedGuide(expandedGuide === "guards" ? null : "guards")}>操作护栏<ChevronDown size={13} /></button> : null}
            {brain?.errorDictionary?.length ? <button type="button" onClick={() => setExpandedGuide(expandedGuide === "errors" ? null : "errors")}>报错字典<ChevronDown size={13} /></button> : null}
            {brain?.launchChecklist?.length ? <button type="button" onClick={() => setExpandedGuide(expandedGuide === "launch" ? null : "launch")}>上线清单<ChevronDown size={13} /></button> : null}
            <button type="button" onClick={() => setExpandedGuide(expandedGuide === "more" ? null : "more")}>更多问法<ChevronDown size={13} /></button>
          </div>
          {expandedGuide === "fields" && brain?.currentPage?.fields.length ? <div className="assistant-field-list">
            <strong>想问哪个字段？</strong>
            <div>{brain.currentPage.fields.slice(0, 4).map((field) => <button key={field.name} type="button" onClick={() => void send(`请用小白能懂的话解释「${field.name}」怎么填，给示例、限制、填错影响。`)}>{field.name}</button>)}</div>
          </div> : null}
          {expandedGuide === "trouble" && brain?.troubleshooting?.length ? <div className="assistant-troubleshoot">
            <strong>哪里卡住了？</strong>
            {brain.troubleshooting.slice(0, 3).map((item) => <button key={item.symptom} type="button" onClick={() => void send(`我遇到这个问题：${item.symptom}。请按检查顺序一步步教我。`)}>{friendlyTroubleLabel(item.symptom)}</button>)}
          </div> : null}
          {expandedGuide === "terms" && brain?.termDictionary?.length ? <div className="assistant-field-list">
            <strong>哪个词看不懂？</strong>
            <div>{brain.termDictionary.slice(0, 10).map((item) => <button key={item.term} type="button" onClick={() => void send(`请用小白能懂的话解释「${item.term}」：一句话说明、生活类比、在哪个页面会看到、误操作有什么风险。`)}>{item.term}</button>)}</div>
          </div> : null}
          {expandedGuide === "guards" && brain?.guardrails?.length ? <div className="assistant-field-list">
            <strong>点之前先问</strong>
            <div>{brain.guardrails.slice(0, 7).map((item) => <button key={item.action} type="button" onClick={() => void send(`我要做「${item.action}」。请按高风险操作护栏告诉我执行前检查、风险、原因文案和执行后验收。`)}>{item.action}</button>)}</div>
          </div> : null}
          {expandedGuide === "errors" && brain?.errorDictionary?.length ? <div className="assistant-field-list">
            <strong>看到报错先点这里</strong>
            <div>{brain.errorDictionary.slice(0, 7).map((item) => <button key={item.keyword} type="button" onClick={() => void send(`我遇到这个报错或现象：${item.keyword}。请翻译成人话，并告诉我先查哪里。`)}>{item.keyword}</button>)}</div>
          </div> : null}
          {expandedGuide === "launch" && brain?.launchChecklist?.length ? <div className="assistant-field-list">
            <strong>上线前后检查</strong>
            <div>{brain.launchChecklist.slice(0, 7).map((item) => <button key={`${item.stage}-${item.item}`} type="button" onClick={() => void send(`请解释上线检查项：${item.item}。通过标志是：${item.passSignal}。我应该怎么确认？`)}>{launchStageLabel(item.stage)} · {item.item}</button>)}</div>
          </div> : null}
          {expandedGuide === "more" && <div className="assistant-quick">{[...(brain?.suggestedPrompts ?? []), ...quickPrompts].slice(0, 7).map((prompt) => <button key={prompt} onClick={() => void send(prompt)}>{prompt}</button>)}</div>}
        </div>}
        {messages.map((message, index) => <div key={index} className={`assistant-bubble ${message.role}`}>
          {message.images?.length ? <div className="assistant-image-strip">{message.images.map((image) => <img key={`${image.name}-${image.dataUrl.length}`} src={image.dataUrl} alt={image.name} />)}</div> : null}
          <span>{message.content}</span>
          {message.role === "assistant" && message.provenance ? <AssistantAnswerEvidence provenance={message.provenance} onAction={handleAssistantAction} /> : null}
        </div>)}
        {busy && messages.at(-1)?.role !== "assistant" && <div className="assistant-bubble assistant assistant-thinking">正在思考，为了更好地回答你，我在整理当前页面信息…</div>}
      </div>
      {error && <div className="form-error assistant-error">{error}</div>}
      {images.length ? <div className="assistant-attachments">{images.map((image, index) => <button type="button" key={`${image.name}-${index}`} onClick={() => setImages((current) => current.filter((_, itemIndex) => itemIndex !== index))} title="移除图片"><img src={image.dataUrl} alt={image.name} /><span>{image.name}</span><X size={12} /></button>)}</div> : null}
      <form className="assistant-input" onSubmit={submit}>
        <button type="button" className="icon-button assistant-input-tool" onClick={() => fileRef.current?.click()} disabled={busy || images.length >= 3} aria-label="上传图片" title="上传图片"><ImagePlus size={17} /></button>
        <input ref={fileRef} className="assistant-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={(event) => void addImages(event)} />
        <input value={input} onChange={(event) => setInput(event.target.value)} onPaste={(event) => void pasteImages(event)} placeholder="例如：这个字段填什么？保存失败怎么办？也可以粘贴图片" maxLength={4000} disabled={busy} aria-label="向 AI 助手提问" />
        <button className="button primary" type="submit" disabled={busy || (!input.trim() && images.length === 0)}><Send size={14} />发送</button>
      </form>
    </div>}
    {!open && <button className={`assistant-fab ${radar?.commandCenter.posture ?? "stable"}`} onClick={() => setOpen(true)} aria-label="打开 AI 助手" title="AI 平台助手"><Bot size={21} />{radar && <span>{radar.commandCenter.posture === "blocked" ? "!" : radar.commandCenter.posture === "watch" ? "待" : "✓"}</span>}</button>}
  </>;
}

function AssistantAnswerEvidence({ provenance, onAction }: { provenance: AssistantProvenance; onAction: (action: AssistantNextAction) => void }) {
  const firstSource = provenance.sources[0];
  return <div className="assistant-answer-evidence" aria-label="回答依据">
    <div className="assistant-answer-evidence-head">
      <strong>{provenance.readOnly ? "只读回答" : "需要人工确认"}</strong>
      <span>{provenance.speculation.hasSpeculation ? "包含推测" : "未标记推测"}</span>
    </div>
    {firstSource ? <dl className="assistant-answer-evidence-list">
      <div><dt>来源页面</dt><dd>{firstSource.pageTitle}</dd></div>
      <div><dt>数据更新时间</dt><dd>{formatAssistantTime(firstSource.dataUpdatedAt)}</dd></div>
      <div><dt>依据</dt><dd>{firstSource.evidenceLabel}</dd></div>
      <div><dt>推测说明</dt><dd>{provenance.speculation.hasSpeculation ? provenance.speculation.notes.join("；") : "本次回答没有显式推测，仍以页面当前状态和审计证据为准。"}</dd></div>
    </dl> : null}
    {provenance.nextActions.length ? <div className="assistant-answer-evidence-actions">
      {provenance.nextActions.map((action) => <button key={`${action.kind}-${action.label}`} type="button" onClick={() => onAction(action)}>{action.label}</button>)}
    </div> : null}
  </div>;
}

function readImageFile(file: File): Promise<ChatImage> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, mimeType: file.type, dataUrl: String(reader.result) });
    reader.onerror = () => reject(new Error("图片读取失败"));
    reader.readAsDataURL(file);
  });
}

function formatAssistantTime(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "页面刚刚返回，时间待确认";
  return new Intl.DateTimeFormat("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function appendAssistantDelta(messages: ChatMessage[], delta: string): ChatMessage[] {
  const last = messages.at(-1);
  if (last?.role === "assistant") return [...messages.slice(0, -1), { ...last, content: last.content + delta }];
  return [...messages, { role: "assistant", content: delta }];
}

function assistantProvenance(response: AssistantChatResponse): AssistantProvenance {
  return {
    readOnly: response.readOnly,
    sources: response.sources,
    speculation: response.speculation,
    nextActions: response.nextActions,
  };
}

function attachAssistantProvenance(messages: ChatMessage[], response: AssistantChatResponse): ChatMessage[] {
  const provenance = assistantProvenance(response);
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message?.role === "assistant") {
      return [
        ...messages.slice(0, index),
        { ...message, provenance },
        ...messages.slice(index + 1),
      ];
    }
  }
  return [...messages, { role: "assistant", content: response.reply, provenance }];
}

async function revealAssistantReply(base: ChatMessage[], reply: string, onPartial: (partial: string) => void) {
  let partial = "";
  for (const char of reply) {
    partial += char;
    onPartial(partial);
    await new Promise((resolve) => window.setTimeout(resolve, /[，。！？\n]/.test(char) ? 35 : 12));
  }
  onPartial(reply);
}

async function streamAssistantReply(messages: ChatMessage[], pagePath: string, onDelta: (delta: string) => void, onMeta: (provider: string, model: string) => void): Promise<AssistantChatResponse> {
  const response = await fetch("/api/assistant/chat/stream", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json", ...csrfHeader() },
    body: JSON.stringify({ messages: messages.slice(-6), pagePath }),
  });
  if (!response.ok || !response.body) throw new Error("AI 流式通道暂时不可用。");
  const decoder = new TextDecoder();
  const reader = response.body.getReader();
  let buffer = "";
  let received = false;
  let finalResponse: AssistantChatResponse | null = null;
  const consumeEvent = (raw: string) => {
    const event = raw.split(/\r?\n/).find((line) => line.startsWith("event:"))?.slice(6).trim() ?? "message";
    const data = raw.split(/\r?\n/).filter((line) => line.startsWith("data:")).map((line) => line.slice(5).trim()).join("\n");
    if (!data) return;
    const payload = JSON.parse(data) as Partial<AssistantChatResponse> & { content?: string; message?: string };
    if (event === "meta" && payload.provider && payload.model) onMeta(payload.provider, payload.model);
    if (event === "delta" && payload.content) {
      received = true;
      onDelta(payload.content);
    }
    if (event === "done" && payload.reply && payload.provider && payload.model && payload.readOnly) {
      finalResponse = payload as AssistantChatResponse;
      onMeta(payload.provider, payload.model);
    }
    if (event === "error") throw new Error(payload.message ?? "AI 助手暂时不可用。");
  };
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const events = buffer.split(/\n\n/);
    buffer = events.pop() ?? "";
    for (const event of events) consumeEvent(event);
  }
  buffer += decoder.decode();
  if (buffer.trim()) consumeEvent(buffer);
  if (!received) throw new Error("AI 没有返回内容。");
  if (!finalResponse) throw new Error("AI 回答缺少来源与更新时间，请稍后重试。");
  return finalResponse;
}

function beginnerPageSummary(summary: string) {
  return summary
    .replace("管理租户从准备中到运行、暂停、恢复、归档的整个生命周期，也是新手最容易误操作的页面。", "这个页面就是管客户账号能不能用。新客户先建租户，检查通过后才能激活；出问题时也在这里暂停或恢复。")
    .replace("维护总管后台和 ERP 智能体可用的大模型服务商、Base URL、API Key 和默认模型。", "这个页面就是给 AI 助手接大模型。先保存服务商配置，再点设为默认，助手才会用它回答。");
}

function priorityLabel(level: "now" | "today" | "later") {
  if (level === "now") return "现在";
  if (level === "today") return "今天";
  return "稍后";
}

function queueLabel(level: "now" | "today" | "week" | "watch") {
  if (level === "now") return "现在";
  if (level === "today") return "今天";
  if (level === "week") return "本周";
  return "观察";
}

function taskLaneLabel(lane: "must" | "should" | "week" | "watch") {
  if (lane === "must") return "必做";
  if (lane === "should") return "建议";
  if (lane === "week") return "本周";
  return "观察";
}

function scopeLabel(scope: NonNullable<AssistantContextResponse["platformWideBrain"]>["domains"][number]["scope"]) {
  if (scope === "control-plane") return "总管";
  if (scope === "tenant") return "租户";
  if (scope === "runtime") return "运行";
  if (scope === "api") return "接口";
  if (scope === "deployment") return "上线";
  if (scope === "security") return "安全";
  return "代管";
}

function launchStageLabel(stage: "before" | "during" | "after" | "rollback") {
  if (stage === "before") return "上线前";
  if (stage === "during") return "上线中";
  if (stage === "after") return "上线后";
  return "回滚";
}

function briefingLabel(stance: "green" | "yellow" | "red") {
  if (stance === "green") return "可推进";
  if (stance === "yellow") return "需盯住";
  return "先止损";
}

function digestLabel(level: "green" | "yellow" | "red") {
  if (level === "green") return "正常";
  if (level === "yellow") return "提醒";
  return "预警";
}

function alertLabel(level: "info" | "warning" | "critical") {
  if (level === "info") return "信息";
  if (level === "warning") return "提醒";
  return "紧急";
}

function warRoomLabel(severity: "none" | "sev3" | "sev2" | "sev1") {
  if (severity === "sev1") return "一级";
  if (severity === "sev2") return "二级";
  if (severity === "sev3") return "三级";
  return "待命";
}

function decisionLabel(decision: "go" | "hold" | "check") {
  if (decision === "go") return "可做";
  if (decision === "hold") return "先别做";
  return "先检查";
}

function severityLabel(severity: "low" | "medium" | "high") {
  if (severity === "high") return "高";
  if (severity === "medium") return "中";
  return "低";
}

function cadenceLabel(cadence: "morning" | "noon" | "evening" | "weekly") {
  if (cadence === "morning") return "早";
  if (cadence === "noon") return "中";
  if (cadence === "evening") return "晚";
  return "周";
}

function friendlyTroubleLabel(label: string) {
  if (label === "AI 助手不可用") return "助手没反应怎么办";
  if (label === "按钮点不了") return "按钮为什么不能点";
  if (label === "保存失败") return "保存失败先查什么";
  return label;
}
